import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { projects } from "../../mock";
import { Eyebrow, SectionTitle } from "./Sections";
import { BeforeAfter, ClientQuote } from "./ProjectExtras";

function Lightbox({ images, index, setIndex }) {
  const open = index !== null;
  const go = useCallback((d) => setIndex((i) => (i + d + images.length) % images.length), [images.length, setIndex]);
  useEffect(() => {
    if (!open) return;
    const k = (e) => { if (e.key === "ArrowRight") go(1); if (e.key === "ArrowLeft") go(-1); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [open, go]);
  const img = open ? images[index] : null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && setIndex(null)}>
      <DialogContent className="max-w-6xl bg-black/95 border-white/10 text-white p-4 sm:p-6" data-testid="gallery-lightbox">
        <DialogTitle className="sr-only">Project photo</DialogTitle>
        <DialogDescription className="sr-only">Use arrows to browse photos</DialogDescription>
        {img && (
          <>
            <div className="relative">
              <img key={img.src} src={img.src} alt={img.caption} className="w-full max-h-[70vh] object-contain rounded-xl animate-in fade-in duration-300" />
              <button onClick={() => go(-1)} data-testid="lightbox-prev" className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 border border-white/20 flex items-center justify-center hover:bg-yellow-400 hover:text-black transition-colors"><Icons.ChevronLeft className="w-5 h-5" /></button>
              <button onClick={() => go(1)} data-testid="lightbox-next" className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 border border-white/20 flex items-center justify-center hover:bg-yellow-400 hover:text-black transition-colors"><Icons.ChevronRight className="w-5 h-5" /></button>
            </div>
            <div className="flex items-center justify-between mt-3 text-sm">
              <span className="text-white/80">{img.caption}</span>
              <span className="font-mono-x text-yellow-400" data-testid="lightbox-counter">{String(index + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}</span>
            </div>
            <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
              {images.map((g, k) => (
                <button key={k} onClick={() => setIndex(k)} className={`shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 transition-[border-color,opacity] ${k === index ? "border-yellow-400" : "border-transparent opacity-50 hover:opacity-100"}`}>
                  <img src={g.src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Timeline({ items }) {
  const last = items.length - 1;
  return (
    <div className="relative mt-14" data-testid="project-timeline">
      <div className="absolute left-[19px] md:left-1/2 top-0 bottom-0 w-[2px] bg-white/10 md:-translate-x-1/2" />
      <div className="space-y-10">
        {items.map((t, k) => (
          <div key={t.title} className={`reveal relative flex md:items-center ${k % 2 ? "md:flex-row-reverse" : ""}`} style={{ transitionDelay: `${k * 80}ms` }}>
            <div className="absolute left-0 md:left-1/2 md:-translate-x-1/2 w-10 h-10 rounded-full bg-yellow-400 text-black font-mono-x text-xs font-bold flex items-center justify-center shadow-[0_0_0_6px_rgba(250,204,21,0.15)] z-10">
              {k === last ? <Icons.Check className="w-4 h-4" /> : String(k + 1).padStart(2, "0")}
            </div>
            <div className={`pl-16 md:pl-0 md:w-1/2 ${k % 2 ? "md:pl-14" : "md:pr-14 md:text-right"}`}>
              <div className="rounded-3xl bg-zinc-900 border border-white/10 p-6 hover:border-yellow-400/50 transition-colors duration-300">
                <span className="font-mono-x text-yellow-400 text-xs tracking-widest">{t.date}</span>
                <h3 className="text-white font-bold text-xl mt-2">{t.title}</h3>
                <p className="text-white/55 mt-2 leading-relaxed">{t.text}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ProjectDetail({ onQuote }) {
  const { slug } = useParams();
  const idx = projects.findIndex((p) => p.slug === slug);
  const p = projects[idx];
  const [lb, setLb] = useState(null);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!p) {
    return (
      <section className="min-h-screen pt-40 text-center px-6" data-testid="project-not-found">
        <h1 className="font-display text-5xl font-bold text-white">Project not found</h1>
        <Link to="/" state={{ scrollTo: "projects" }} className="inline-flex mt-8 bg-yellow-400 text-black font-semibold px-8 py-3 rounded-2xl">Back to projects</Link>
      </section>
    );
  }

  const prev = projects[(idx - 1 + projects.length) % projects.length];
  const next = projects[(idx + 1) % projects.length];
  const stats = [[Icons.User, "Client", p.client], [Icons.Clock, "Duration", p.duration], [Icons.DollarSign, "Contract Value", p.value], [Icons.Ruler, "Size", p.size]];

  return (
    <div data-testid="project-detail">
      <section className="relative h-[78vh] min-h-[560px] pt-20 overflow-hidden grain">
        <img src={p.image.replace("w=1200", "w=2000")} alt={p.title} className="absolute inset-0 w-full h-full object-cover animate-in zoom-in-105 duration-[1500ms]" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/30" />
        <div className="relative h-full max-w-[1260px] mx-auto px-6 lg:px-10 flex flex-col justify-end pb-16">
          <Link to="/" state={{ scrollTo: "projects" }} data-testid="back-to-projects" className="group inline-flex items-center gap-2 text-white/70 hover:text-yellow-400 text-sm tracking-[0.15em] w-fit transition-colors">
            <Icons.ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" /> ALL PROJECTS
          </Link>
          <span className="mt-6 w-fit bg-yellow-400 text-black text-[12px] font-bold tracking-[0.12em] rounded-full px-4 py-1.5 uppercase">{p.category}</span>
          <h1 className="font-display font-bold text-white uppercase text-[44px] sm:text-[72px] lg:text-[84px] leading-[0.92] tracking-[-0.04em] mt-5" data-testid="project-title">{p.title}</h1>
          <div className="flex flex-wrap gap-6 mt-6 text-white/75">
            <span className="flex items-center gap-2"><Icons.MapPin className="w-4 h-4 text-yellow-400" />{p.location}</span>
            <span className="flex items-center gap-2"><Icons.Calendar className="w-4 h-4 text-yellow-400" />Completed {p.year}</span>
          </div>
        </div>
      </section>

      <section className="bg-black">
        <div className="max-w-[1260px] mx-auto px-6 lg:px-10 grid grid-cols-2 lg:grid-cols-4 border-y border-white/10">
          {stats.map(([I, l, v], k) => (
            <div key={l} className={`py-8 px-2 sm:px-6 ${k ? "lg:border-l border-white/10" : ""}`}>
              <I className="w-5 h-5 text-yellow-400" />
              <div className="text-white/45 text-[12px] tracking-[0.15em] uppercase mt-3">{l}</div>
              <div className="text-white font-display text-2xl font-bold mt-1">{v}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-black py-24">
        <div className="max-w-[1260px] mx-auto px-6 lg:px-10 grid lg:grid-cols-[1.2fr_0.8fr] gap-14">
          <div className="reveal">
            <Eyebrow>Overview</Eyebrow>
            <SectionTitle>About the Project</SectionTitle>
            <p className="text-white/70 text-lg leading-relaxed mt-8">{p.description}</p>
            <p className="text-white/55 leading-relaxed mt-5">
              Our team managed every phase in-house, from early budgeting through final handover, keeping the client informed with weekly site reports and a shared schedule.
            </p>
          </div>
          <div className="reveal rounded-3xl bg-zinc-900 border border-white/10 p-8">
            <h3 className="text-white font-bold text-xl">Scope of Work</h3>
            <ul className="mt-5 space-y-4">
              {p.scope.map((s) => (
                <li key={s} className="flex items-start gap-3 text-white/75">
                  <span className="mt-0.5 w-6 h-6 rounded-full bg-yellow-400/15 flex items-center justify-center shrink-0"><Icons.Check className="w-3.5 h-3.5 text-yellow-400" /></span>{s}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-black pb-24">
        <div className="max-w-[1260px] mx-auto px-6 lg:px-10">
          <div className="reveal flex flex-wrap items-end justify-between gap-4">
            <div><Eyebrow>Transformation</Eyebrow><SectionTitle>Before &amp; After</SectionTitle></div>
            <p className="text-white/55 max-w-[380px]">Drag the handle to see how the space changed from bare structure to finished result.</p>
          </div>
          <div className="reveal mt-12"><BeforeAfter {...p.beforeAfter} /></div>
          <div className="mt-10"><ClientQuote t={p.testimonial} /></div>
        </div>
      </section>

      <section className="bg-[#141416] py-24">
        <div className="max-w-[1260px] mx-auto px-6 lg:px-10">
          <div className="reveal flex flex-wrap items-end justify-between gap-4">
            <div><Eyebrow>Gallery</Eyebrow><SectionTitle>Project Photos</SectionTitle></div>
            <span className="font-mono-x text-white/45 text-sm">{p.gallery.length} PHOTOS</span>
          </div>
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 auto-rows-[200px] lg:auto-rows-[230px] gap-4" data-testid="project-gallery">
            {p.gallery.map((g, k) => (
              <button
                key={k}
                onClick={() => setLb(k)}
                data-testid={`gallery-item-${k}`}
                className={`group relative rounded-3xl overflow-hidden ${k === 0 ? "col-span-2 row-span-2" : ""} ${k === 3 || k === 5 ? "lg:col-span-2" : ""}`}
              >
                <img src={g.src} alt={g.caption} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-300">
                  <span className="text-white text-sm font-medium">{g.caption}</span>
                  <span className="w-9 h-9 rounded-full bg-yellow-400 flex items-center justify-center"><Icons.Maximize2 className="w-4 h-4 text-black" /></span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-black py-24">
        <div className="max-w-[1060px] mx-auto px-6 lg:px-10">
          <div className="reveal text-center">
            <Eyebrow>From groundbreaking to handover</Eyebrow>
            <div className="flex justify-center"><h2 className="font-display font-bold text-white text-[40px] sm:text-[54px] tracking-[-0.03em] leading-tight mt-3">Build Timeline</h2></div>
          </div>
          <Timeline items={p.timeline} />
        </div>
      </section>

      <section className="bg-[#141416] py-20">
        <div className="max-w-[1260px] mx-auto px-6 lg:px-10">
          <div className="rounded-3xl bg-yellow-400 p-10 sm:p-14 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <h3 className="font-display text-black font-bold text-3xl sm:text-4xl tracking-tight">Planning something similar?</h3>
              <p className="text-black/70 mt-2">Tell us about your project and get a tailored estimate.</p>
            </div>
            <button onClick={onQuote} data-testid="detail-start-project" className="group flex items-center gap-3 bg-black text-white font-semibold px-8 py-4 rounded-2xl hover:bg-zinc-800 transition-colors w-fit">
              START A PROJECT <Icons.ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
          <div className="grid sm:grid-cols-2 gap-6 mt-10">
            {[[prev, "Previous Project", Icons.ArrowLeft], [next, "Next Project", Icons.ArrowRight]].map(([q, l, I], k) => (
              <Link key={l} to={`/projects/${q.slug}`} data-testid={k ? "next-project" : "prev-project"} className={`group relative h-[180px] rounded-3xl overflow-hidden flex items-end p-6 ${k ? "sm:justify-end sm:text-right" : ""}`}>
                <img src={q.image} alt={q.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-black/60 group-hover:bg-black/45 transition-colors" />
                <div className="relative">
                  <span className={`flex items-center gap-2 text-yellow-400 text-xs tracking-[0.15em] uppercase ${k ? "sm:justify-end" : ""}`}>{!k && <I className="w-3.5 h-3.5" />}{l}{k ? <I className="w-3.5 h-3.5" /> : null}</span>
                  <span className="block text-white font-bold text-2xl mt-1">{q.title}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Lightbox images={p.gallery} index={lb} setIndex={setLb} />
    </div>
  );
}
